import React, { useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
    PRODUCT_TYPE_MOVIE_REWARDS,
    PRODUCT_TYPE_VOUCHER_MANAGER,
    TYPE_GIFTING_VOUCHER,
    TYPE_SEASONAL_VOUCHER,
} from "@config/config";
import usePurchaseTracking from "@hooks/usePurchaseTracking";
import { ApplicationContext } from "@context/ApplicationContext";
import { BookSessionContext } from "@context/BookSessionContext";
import { PromoContext } from "@context/PromoContext";
import { TicketContext } from "@context/TicketContext";
import AlertMessage from "@components/AlertMessage";
import ConfirmationMessage from "@components/ConfirmationMessage";
import FeatureFilm from "@components/FeatureFilm";
import Loader from "@components/Ui/Loader";
import VoucherConfirmation from "@components/VoucherConfirmation";
import VersionedLayout from "@components/Wrappers/VersionedLayout";


const SuccessPage = () => {
    const { uuid } = useParams();
    const { productTypeId } = useContext(ApplicationContext);

    const {
        setBookingSessionId,
        setUuid,
        setCheckout,
        setSelectedItems,
        setPromoCodeSuccess,
        setPromoCodeMessage,
        setReferralCode,
        setReferralSuccess,
        setError,
        setProceedToCheckout
    } = useContext(BookSessionContext);

    const {
        setSelectedPromo,
        setAppliedPromo,
        setStandardPromoCode,
    } = useContext(PromoContext);

    const {
        isLoading,
        errorMessage,
        fetchTicket,
        purchasedTicket,
        setPurchasedTicket,
        purchasedSession,
        purchasedVoucher,
        setPurchasedVoucher,
        setPurchasedSession,
        tempAccountCreation,
        setTempAccountCreation,
        purchasedType,
        isTicketingSystemPurchase,
        setIsTicketingSystemPurchase,
    } = useContext(TicketContext);

    const isMovieRewards   = productTypeId === PRODUCT_TYPE_MOVIE_REWARDS;
    const isVoucherManager = productTypeId === PRODUCT_TYPE_VOUCHER_MANAGER;

    useEffect(() => {
        fetchTicket(uuid);

        setBookingSessionId(null);
        setUuid(null);
        setCheckout(false);
        setSelectedItems([]);
        setPromoCodeSuccess(null);
        setReferralCode("");
        setReferralSuccess(null);
        setError("");
        setPromoCodeMessage("");
        setSelectedPromo(null);
        setAppliedPromo(null);
        setStandardPromoCode("");
        setProceedToCheckout(false);

        return () => {
            setPurchasedTicket(null);
            setPurchasedVoucher(null);
            setPurchasedSession(null);
            setIsTicketingSystemPurchase(null);
            setTempAccountCreation(false);
        };
    }, [uuid]);

    // Google Analytics tracking — fires when purchase data is ready
    usePurchaseTracking(purchasedTicket, purchasedSession);

    if (!productTypeId) {
        return <Loader isLoading={true} />;
    }

    if (isLoading) {
        return <Loader isLoading={isLoading} />;
    }

    const isVoucherOnlyPurchase = purchasedType === TYPE_SEASONAL_VOUCHER || purchasedType === TYPE_GIFTING_VOUCHER;

    const showConfirmationMessage =
        isMovieRewards &&
        purchasedTicket &&
        purchasedSession &&
        !isVoucherOnlyPurchase;

    const showVoucherConfirmation = (isMovieRewards && isVoucherOnlyPurchase) || isVoucherManager;

    return (
        <VersionedLayout>

            {showConfirmationMessage && (
                <ConfirmationMessage
                    purchasedTicket={purchasedTicket}
                    purchasedSession={purchasedSession}
                    tempAccountCreation={tempAccountCreation}
                    isTicketingSystemPurchase={isTicketingSystemPurchase}
                />
            )}

            {showVoucherConfirmation && (
                <VoucherConfirmation purchasedVoucher={purchasedVoucher} />
            )}

            {!showConfirmationMessage && !showVoucherConfirmation && !errorMessage && (
                <AlertMessage
                    type="error"
                    message="Unable to display your confirmation"
                    messageList={["Please check your email for purchase details or contact support."]}
                />
            )}

            {errorMessage && (
                <AlertMessage
                    type="error"
                    message="Error Occurred"
                    messageList={errorMessage}
                />
            )}

            <FeatureFilm />
        </VersionedLayout>
    );
};

export default SuccessPage;
